import React, { useState } from 'react';
import { Moon, X as XIcon } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';

const PRESETS = [5, 15, 30, 45, 60, 90];

const formatRemaining = (ms) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m >= 1) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

/**
 * SleepTimerButton — moon icon that opens a small popover offering
 * 5/15/30/45/60/90 min presets, "End of track", and "Off". Active state shows
 * a purple ring + live countdown so listeners always know when audio will
 * stop. Designed to drop into the Player toolbar; size variant lets the
 * MiniPlayer use a compact pill.
 */
const SleepTimerButton = ({ variant = 'default' }) => {
  const {
    sleepTimerActive,
    sleepTimerRemainingMs,
    sleepTimerEndOfTrack,
    setSleepTimer,
  } = useAudioPlayer();
  const [open, setOpen] = useState(false);

  const handlePick = (option) => {
    // Distinct buzz for "setting/clearing the sleep timer" — 12ms feels
    // like a softer thump than the play/pause confirmation, communicates
    // commitment without being aggressive.
    try { navigator.vibrate?.(option == null ? 14 : 10); } catch { /* ignore */ }
    setSleepTimer(option);
    setOpen(false);
  };

  const compact = variant === 'compact';
  const triggerLabel = sleepTimerEndOfTrack
    ? 'End of track'
    : sleepTimerRemainingMs > 0
    ? formatRemaining(sleepTimerRemainingMs)
    : 'Sleep';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={
            compact
              ? `flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-mono transition-colors ${
                  sleepTimerActive
                    ? 'bg-purple-600/30 text-purple-200 ring-1 ring-purple-400/50'
                    : 'bg-slate-700/60 text-gray-300 hover:bg-slate-700'
                }`
              : `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  sleepTimerActive
                    ? 'bg-purple-600/20 text-purple-200 ring-1 ring-purple-500/40'
                    : 'bg-slate-700/60 text-gray-300 hover:bg-slate-700 hover:text-white'
                }`
          }
          aria-label={sleepTimerActive ? `Sleep timer: ${triggerLabel}` : 'Set sleep timer'}
          data-testid="sleep-timer-trigger"
        >
          <Moon className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          <span className="tabular-nums">{triggerLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-60 p-3 bg-slate-900 border border-slate-700 text-white"
        data-testid="sleep-timer-popover"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-[0.2em] text-purple-300 font-semibold">
            Sleep Timer
          </p>
          {sleepTimerActive && (
            <button
              type="button"
              onClick={() => handlePick(null)}
              className="text-[11px] text-gray-400 hover:text-white inline-flex items-center gap-1"
              data-testid="sleep-timer-off"
            >
              <XIcon className="w-3 h-3" />
              Off
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-2">
          {PRESETS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => handlePick({ minutes: m })}
              className="px-2 py-2 rounded-md bg-slate-800 hover:bg-purple-600 text-sm font-medium transition-colors"
              data-testid={`sleep-timer-${m}m`}
            >
              {m}m
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => handlePick({ endOfTrack: true })}
          className={`w-full px-2 py-2 rounded-md text-sm font-medium transition-colors ${
            sleepTimerEndOfTrack
              ? 'bg-purple-600 text-white'
              : 'bg-slate-800 hover:bg-purple-600/80 text-gray-200'
          }`}
          data-testid="sleep-timer-end-of-track"
        >
          End of current track
        </button>

        {sleepTimerActive && !sleepTimerEndOfTrack && (
          <p
            className="mt-3 text-center text-xs text-purple-200 font-mono tabular-nums"
            data-testid="sleep-timer-countdown"
          >
            Pausing in {formatRemaining(sleepTimerRemainingMs)}
          </p>
        )}
        {sleepTimerEndOfTrack && (
          <p
            className="mt-3 text-center text-xs text-purple-200"
            data-testid="sleep-timer-end-of-track-active"
          >
            Audio will stop when this track ends.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SleepTimerButton;
